import { FirebaseError } from "firebase/app";
import {
  browserLocalPersistence,
  browserSessionPersistence,
  sendPasswordResetEmail,
  setPersistence,
  signInWithEmailAndPassword,
} from "firebase/auth";
import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";

import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { firebaseAuth } from "~/lib/firebase";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface FormState {
  email: string;
  password: string;
  rememberMe: boolean;
}

const INITIAL_FORM: FormState = {
  email: "",
  password: "",
  rememberMe: true,
};

interface FieldErrors {
  email?: string;
  password?: string;
}

function validate(form: FormState): FieldErrors {
  const errors: FieldErrors = {};
  if (!form.email) errors.email = "Required";
  else if (!EMAIL_PATTERN.test(form.email)) errors.email = "Enter a valid email";
  if (!form.password) errors.password = "Required";
  return errors;
}

// Only honour same-origin relative paths so `?next=` can't be used as an open
// redirect to an attacker-controlled site. Must start with a single "/".
function safeNext(next: string | null): string {
  if (next && next.startsWith("/") && !next.startsWith("//")) return next;
  return "/";
}

// Map Firebase auth error codes to user-facing copy. Crucially, every
// credential-mismatch code collapses to one message so we never leak whether
// the email or the password was the wrong half.
function messageForAuthError(err: unknown): string {
  const code = err instanceof FirebaseError ? err.code : "";
  switch (code) {
    case "auth/invalid-credential":
    case "auth/invalid-email":
    case "auth/user-not-found":
    case "auth/wrong-password":
      return "Email or password is wrong";
    case "auth/user-disabled":
      return "This account has been disabled. Contact your administrator.";
    case "auth/too-many-requests":
      return "Too many attempts, try again in a few minutes";
    case "auth/network-request-failed":
      return "Network error — check your connection and try again";
    default:
      return "Something went wrong. Try again.";
  }
}

export default function LoginRoute(): JSX.Element {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState<FormState>(INITIAL_FORM);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const update = <K extends keyof FormState>(key: K, value: FormState[K]): void => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setFieldErrors((prev) => ({ ...prev, [key]: undefined }));
    setFormError(null);
    setNotice(null);
  };

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    const errors = validate(form);
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    setSubmitting(true);
    try {
      // Unchecked "Remember me" → session persistence (cleared on tab close).
      // Checked → local persistence (Firebase's default, survives reload).
      await setPersistence(
        firebaseAuth,
        form.rememberMe ? browserLocalPersistence : browserSessionPersistence,
      );
      await signInWithEmailAndPassword(firebaseAuth, form.email.trim(), form.password);
      navigate(safeNext(searchParams.get("next")), { replace: true });
    } catch (err) {
      setFormError(messageForAuthError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const onForgotPassword = async (): Promise<void> => {
    setFormError(null);
    setNotice(null);
    if (!form.email) {
      setFieldErrors((prev) => ({ ...prev, email: "Enter your email to reset your password" }));
      return;
    }
    if (!EMAIL_PATTERN.test(form.email)) {
      setFieldErrors((prev) => ({ ...prev, email: "Enter a valid email" }));
      return;
    }

    setSubmitting(true);
    try {
      await sendPasswordResetEmail(firebaseAuth, form.email.trim());
    } catch (err) {
      // Swallow user-not-found so we don't disclose which emails are registered;
      // surface only genuine failures (rate limit, network).
      if (err instanceof FirebaseError && err.code === "auth/user-not-found") {
        // fall through to the same success notice
      } else {
        setFormError(messageForAuthError(err));
        return;
      }
    } finally {
      setSubmitting(false);
    }
    setNotice("If that email has an account, a password-reset link is on its way.");
  };

  return (
    <main className="flex min-h-svh items-center justify-center bg-background px-6 py-12 text-foreground">
      <div className="w-full max-w-md space-y-6 rounded-lg border border-border bg-card p-6 shadow-sm">
        <header className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight">Log in</h1>
          <p className="text-sm text-muted-foreground">Welcome back to HandoverPro.</p>
        </header>

        <form className="space-y-4" onSubmit={onSubmit} noValidate>
          <Field id="email" label="Email" error={fieldErrors.email}>
            <Input
              id="email"
              type="email"
              autoComplete="email"
              value={form.email}
              onChange={(e) => update("email", e.target.value)}
              aria-invalid={Boolean(fieldErrors.email)}
              required
            />
          </Field>

          <Field id="password" label="Password" error={fieldErrors.password}>
            <Input
              id="password"
              type="password"
              autoComplete="current-password"
              value={form.password}
              onChange={(e) => update("password", e.target.value)}
              aria-invalid={Boolean(fieldErrors.password)}
              required
            />
          </Field>

          <div className="flex items-center justify-between">
            <label className="flex items-center gap-2 text-sm text-muted-foreground">
              <input
                type="checkbox"
                checked={form.rememberMe}
                onChange={(e) => update("rememberMe", e.target.checked)}
                className="h-4 w-4 rounded border-input accent-primary"
              />
              Remember me
            </label>
            <button
              type="button"
              onClick={() => void onForgotPassword()}
              disabled={submitting}
              className="text-sm font-medium text-primary underline-offset-4 hover:underline disabled:opacity-50"
            >
              Forgot password?
            </button>
          </div>

          {formError && (
            <div
              role="alert"
              className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
            >
              {formError}
            </div>
          )}

          {notice && (
            <div
              role="status"
              className="rounded-md border border-border bg-muted px-3 py-2 text-sm text-muted-foreground"
            >
              {notice}
            </div>
          )}

          <Button type="submit" className="w-full" disabled={submitting}>
            {submitting ? "Logging in…" : "Log in"}
          </Button>
        </form>

        <p className="text-center text-sm text-muted-foreground">
          Don&apos;t have an account?{" "}
          <Link
            to="/register"
            className="font-medium text-primary underline-offset-4 hover:underline"
          >
            Register
          </Link>
        </p>
      </div>
    </main>
  );
}

function Field({
  id,
  label,
  error,
  children,
}: {
  id: string;
  label: string;
  error?: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      {children}
      {error && (
        <p id={`${id}-error`} className="text-xs text-destructive">
          {error}
        </p>
      )}
    </div>
  );
}
